import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Eye, FileText, Save, Loader2 } from "lucide-react";
import { api, loadBiodata, saveBiodata, addHistory } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2 } from "lucide-react";
import Navbar from "@/components/Navbar";

const DRAFT_KEY = (id) => `simpa_draft_${id}`;
const KANIM_STORAGE = "simpa_kanim";

const INPUT_TYPE_MAP = { tel: "tel", email: "email" };
function resolveInputType(fieldType) {
  return INPUT_TYPE_MAP[fieldType] || "text";
}

function FieldInput({ field, value, onChange }) {
  const common = {
    id: field.key,
    value: value ?? "",
    required: field.required,
    placeholder: field.placeholder || "",
    onChange: (e) => onChange(field.key, e.target.value),
    "data-testid": `field-${field.key}`,
    className: "mt-2 h-11 rounded-lg",
  };
  if (field.type === "textarea") {
    return <Textarea {...common} className="mt-2 min-h-[100px] rounded-lg" />;
  }
  if (field.type === "select") {
    return (
      <Select value={value ?? ""} onValueChange={(v) => onChange(field.key, v)}>
        <SelectTrigger className="mt-2 h-11 rounded-lg" data-testid={`field-${field.key}`}>
          <SelectValue placeholder="Pilih..." />
        </SelectTrigger>
        <SelectContent>
          {field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "date") {
    return <Input type="date" {...common} />;
  }
  return <Input type={resolveInputType(field.type)} {...common} />;
}

function stepCircleClass(i, step) {
  if (i < step) return "bg-emerald-500 text-white";
  if (i === step) return "bg-blue-600 text-white";
  return "bg-slate-100 text-slate-500";
}

export default function FormWizard() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [kanims, setKanims] = useState([]);
  const [kanimId, setKanimId] = useState(localStorage.getItem(KANIM_STORAGE) || "atambua");
  const [biodata, setBiodata] = useState({});
  const [extra, setExtra] = useState({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/forms/${formId}`).then(({ data }) => {
      setMeta(data);
      const savedBio = loadBiodata();
      const savedDraft = JSON.parse(localStorage.getItem(DRAFT_KEY(formId)) || "{}");
      setBiodata({ ...savedBio, ...(savedDraft.biodata || {}) });
      setExtra(savedDraft.extra || {});
    }).catch(() => {
      toast.error("Formulir tidak ditemukan");
      navigate("/pemohon");
    });
    api.get("/kanim").then(({ data }) => setKanims(data.kanims || []));
  }, [formId, navigate]);

  // Persist selected kanim
  useEffect(() => { localStorage.setItem(KANIM_STORAGE, kanimId); }, [kanimId]);

  // Auto-save draft
  useEffect(() => {
    if (!meta) return;
    localStorage.setItem(DRAFT_KEY(formId), JSON.stringify({ biodata, extra }));
  }, [biodata, extra, formId, meta]);

  const steps = useMemo(() => {
    if (!meta) return [];
    return [
      { title: "Data Diri (Biodata)", desc: "Data ini otomatis mengisi formulir lain", fields: meta.biodata_fields, kind: "biodata" },
      { title: "Detail Formulir", desc: meta.form.description, fields: meta.form.extra_fields, kind: "extra" },
      { title: "Tinjau & Kirim", desc: "Pastikan semua data sudah benar", kind: "review" },
    ];
  }, [meta]);

  const progress = steps.length ? Math.round(((step + 1) / steps.length) * 100) : 0;

  if (!meta) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  const current = steps[step];

  const setBioField = (k, v) => setBiodata((p) => ({ ...p, [k]: v }));
  const setExtraField = (k, v) => setExtra((p) => ({ ...p, [k]: v }));

  const validateStep = () => {
    if (current.kind === "review") return true;
    const missing = current.fields.filter((f) => f.required && !(current.kind === "biodata" ? biodata : extra)[f.key]);
    if (missing.length) {
      toast.error(`Isi field wajib: ${missing.map((f) => f.label).join(", ")}`);
      return false;
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    if (current.kind === "biodata") saveBiodata(biodata);
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const previewPdf = async () => {
    if (!validateStep()) return;
    try {
      const res = await api.post("/preview-pdf", {
        form_id: formId, biodata, extra, kanim_id: kanimId,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Gagal membuat pratinjau");
    }
  };

  const submitFinal = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/submissions", {
        form_id: formId, biodata, extra, applicant_name: biodata.nama, kanim_id: kanimId,
      });
      saveBiodata(biodata);
      localStorage.removeItem(DRAFT_KEY(formId));
      addHistory({
        id: data.id,
        form_id: formId,
        form_title: data.form_title,
        applicant_name: biodata.nama,
        kanim_id: kanimId,
        kanim_name: kanims.find((k) => k.id === kanimId)?.nama_kanim || "",
        created_at: data.created_at,
      });
      toast.success("Formulir berhasil dibuat!");
      navigate(`/preview/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan formulir");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/60">
      <Navbar variant="public" />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/pemohon" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Kembali ke daftar formulir
        </Link>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Sidebar steps */}
          <aside className="lg:col-span-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-6 sticky top-24">
              <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold">{meta.form.code}</div>
              <h2 className="mt-2 text-xl font-semibold text-slate-900 tracking-tight leading-tight">{meta.form.title}</h2>

              {/* Kanim selector */}
              <div className="mt-5 pb-5 border-b border-slate-100">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" /> Kantor Imigrasi Tujuan
                </Label>
                <Select value={kanimId} onValueChange={setKanimId}>
                  <SelectTrigger className="mt-2 h-10 rounded-lg text-sm" data-testid="kanim-select">
                    <SelectValue placeholder="Pilih kantor imigrasi" />
                  </SelectTrigger>
                  <SelectContent>
                    {kanims.map((k) => (
                      <SelectItem key={k.id} value={k.id} data-testid={`kanim-option-${k.id}`}>
                        <div className="flex flex-col py-0.5">
                          <span className="font-medium">{k.nama_kanim}</span>
                          <span className="text-xs text-slate-500">{k.kanwil}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
                  <span>Langkah {step + 1} dari {steps.length}</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} className="h-2" data-testid="wizard-progress" />
              </div>
              <ul className="mt-6 space-y-3">
                {steps.map((s, i) => (
                  <li key={s.title || `step-${i}`} className="flex items-start gap-3">
                    <div className={`mt-0.5 w-6 h-6 rounded-full grid place-items-center text-xs font-semibold flex-shrink-0 ${stepCircleClass(i, step)}`}>
                      {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </div>
                    <div>
                      <div className={`text-sm font-medium ${i === step ? "text-slate-900" : "text-slate-600"}`}>{s.title}</div>
                      <div className="text-xs text-slate-500 leading-relaxed">{s.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-slate-100 text-xs text-slate-500 flex items-center gap-2">
                <Save className="w-3.5 h-3.5" /> Draft tersimpan otomatis di perangkat Anda
              </div>
            </div>
          </aside>

          {/* Main content */}
          <section className="lg:col-span-8">
            <div className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                >
                  <h3 className="text-2xl font-semibold tracking-tight text-slate-900">{current.title}</h3>
                  <p className="mt-1 text-slate-600 text-sm">{current.desc}</p>

                  {current.kind !== "review" ? (
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {current.fields.map((f) => (
                        <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                          <Label htmlFor={f.key} className="text-slate-700 text-sm">
                            {f.label} {f.required && <span className="text-red-500">*</span>}
                          </Label>
                          <FieldInput
                            field={f}
                            value={(current.kind === "biodata" ? biodata : extra)[f.key]}
                            onChange={current.kind === "biodata" ? setBioField : setExtraField}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-8 space-y-6">
                      <div className="rounded-xl bg-slate-50 border border-slate-200 p-5">
                        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Biodata</div>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                          {meta.biodata_fields.map((f) => (
                            <div key={f.key} className="flex justify-between gap-4 border-b border-slate-100 pb-1.5">
                              <dt className="text-slate-500">{f.label}</dt>
                              <dd className="font-medium text-slate-900 text-right">{biodata[f.key] || "—"}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                      <div className="rounded-xl bg-slate-50 border border-slate-200 p-5">
                        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Detail Formulir</div>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                          {meta.form.extra_fields.map((f) => (
                            <div key={f.key} className="flex justify-between gap-4 border-b border-slate-100 pb-1.5">
                              <dt className="text-slate-500">{f.label}</dt>
                              <dd className="font-medium text-slate-900 text-right">{extra[f.key] || "—"}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                      <Button onClick={previewPdf} variant="outline" className="w-full rounded-lg h-11" data-testid="preview-pdf-btn">
                        <Eye className="w-4 h-4 mr-2" /> Pratinjau PDF (buka di tab baru)
                      </Button>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              <div className="mt-10 pt-6 border-t border-slate-100 flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  data-testid="wizard-prev-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-1.5" /> Sebelumnya
                </Button>
                {step < steps.length - 1 ? (
                  <Button onClick={next} className="btn-lift rounded-lg bg-blue-600 hover:bg-blue-700 h-11 px-6" data-testid="wizard-next-btn">
                    Lanjutkan <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                ) : (
                  <Button onClick={submitFinal} disabled={submitting} className="btn-lift rounded-lg bg-blue-600 hover:bg-blue-700 h-11 px-6" data-testid="wizard-submit-btn">
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                    Selesai & Buat PDF
                  </Button>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
